import React, { useEffect, useRef, useState } from "react"

import { Button } from "components/Button"
import { Buttons, Card } from "components/Card/Card"
import { Input } from "components/Input"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { cdn } from "core/cdn"
import { Keyboard } from "hocs/withKeyboard"
import { useLanguage } from "locales"
import { Title } from "components/Title/Title"
import { Message } from "types/redux"

import styles from "../PharmacyOwnerInfo/PharmacyOwnerInfo.module.scss"
import settingsStyles from "./MssSettings.module.scss"

// An MSS address is an email address on a subdomain of mssante.fr (ANS rule EX_GEN_0200);
// the patient mailboxes of Mon espace santé (patient.mssante.fr) are refused. Same rule as
// the API: only these addresses can be stored
export const MSS_EMAIL_PATTERN = /^[a-z0-9._%+-]+@(?!patient\.mssante\.fr$)(?:[a-z0-9-]+\.)+mssante\.fr$/i

interface MssEmailEntryProps {
  initialValue: string
  // The initial value comes from the RPPS directory and is not confirmed yet
  recovered: boolean
  // Let "Confirmer" save the address as shown (a new owner confirming the directory address)
  confirmUnchanged?: boolean
  loading?: boolean
  saveError?: Message
  onBack: () => void
  onConfirm: (email: string) => void
  // Shown to a new owner only: leave without confirming the address. With an address shown, a
  // warning asks first and skipping it is a refusal (optOut true); with no address, nothing is stored
  onSkip?: (optOut: boolean) => void
  // "popup" inside the "Espace professionnel" modal (fixed size), "page" as a full screen of a
  // flow, laid out like the other entry screens
  layout?: "popup" | "page"
}

// Entry of the pharmacy MSS address, with the MSSanté keyboard and the domain check
const MssEmailEntry: React.FC<MssEmailEntryProps> = ({
  initialValue,
  recovered,
  confirmUnchanged = false,
  loading,
  saveError,
  onBack,
  onConfirm,
  onSkip,
  layout = "popup",
}) => {
  const { languages } = useLanguage()
  const [value, setValue] = useState(initialValue)
  const [inputError, setInputError] = useState<Message | null>(null)
  const [skipWarningVisible, setSkipWarningVisible] = useState(false)
  // Skipping an address found for the RPPS is a refusal of the communications; skipping with no
  // address known stores nothing
  const hasAddressToConfirm = !!initialValue.trim()
  const keyboardRef = useRef<Keyboard>(null)

  // The keyboard keeps its own copy of the text and is not seeded with the prefilled value:
  // align it so typing continues the address instead of replacing it
  useEffect(() => {
    keyboardRef.current?.keyboard?.setInput(value)
  }, [value])

  const submit = () => {
    if (loading) return
    const trimmed = value.trim()
    if (!trimmed) return
    setSkipWarningVisible(false)
    if (!MSS_EMAIL_PATTERN.test(trimmed)) {
      setInputError({
        type: "error",
        text: (
          // One span: the message container lays out its children as flex items
          <span>
            {languages.ownerInfoMssInvalidDomainPrefix} <b>{languages.ownerInfoMssDomain}</b>
          </span>
        ),
      })
      return
    }
    setInputError(null)
    onConfirm(trimmed)
  }

  return (
    <>
      <PreviousButton onClick={onBack} />
      <Card message={inputError ?? saveError ?? undefined}>
        <div className={styles.Heading}>
          <span className={styles.MssHeading}>
            {recovered ? languages.ownerInfoMssRecoveredHeading : languages.ownerInfoMssEnterHeading}
          </span>
        </div>
        <Keyboard
          ref={keyboardRef}
          // The popup has a fixed size: drop the shared keyboard's minimum height there
          containerStyle={layout === "popup" ? { minHeight: 0 } : undefined}
          options={{ type: "mss_email", inputName: "mss_email" }}
          value={value}
          onChange={(val: string) => {
            setValue(val)
            setInputError(null)
          }}
        >
          <div className={styles.InputWrapper}>
            <Input placeholder={languages.ownerInfoMssPlaceholder} value={value} name="mss_email" />
          </div>
        </Keyboard>
        <Buttons>
          {onSkip && (
            <Button
              className={styles.WideButton}
              onClick={() => setSkipWarningVisible(true)}
              disabled={loading}
            >
              {languages.ownerInfoMssSkipAccept.toUpperCase()}
            </Button>
          )}
          <Button
            type="primary"
            className={styles.ConfirmButton}
            onClick={submit}
            loading={loading}
            // Like the RPPS: nothing to save while the address shown has not been changed, so a
            // directory address stays unsaved and keeps following the directory
            disabled={!value.trim() || loading || (!confirmUnchanged && value.trim() === initialValue.trim())}
          >
            {(onSkip ? languages.next : languages.confirm).toUpperCase()}
          </Button>
        </Buttons>
      </Card>
      {onSkip && (
        <GenericModal
          visible={skipWarningVisible}
          style={{ width: 1550 }}
          // Title and pictogram live in the body, so the popup is exactly as tall as its content
          bodyStyle={{ textAlign: "center", padding: "40px 0 0" }}
          onMaskClick={() => setSkipWarningVisible(false)}
          footer={
            <div className={styles.SkipWarningFooter}>
              <Button
                type="primary"
                wide="long"
                disabled={loading}
                // Back to the field to confirm or type the address
                onClick={() => setSkipWarningVisible(false)}
              >
                {languages.ownerInfoMssMissingCta.toUpperCase()}
              </Button>
              <span
                className="small-link"
                style={{ marginTop: 24 }}
                onClick={() => {
                  if (loading) return
                  setSkipWarningVisible(false)
                  onSkip(hasAddressToConfirm)
                }}
              >
                {languages.ownerInfoMssSkipAccept}
              </span>
            </div>
          }
        >
          <div>
            <div className={settingsStyles.WarningTitle}>
              <img src={cdn("/images/warning_picto.svg")} alt="" width={140} height={140} />
              <Title size="medium" text={languages.ownerInfoMssMissingTitle} />
            </div>
            <p className={styles.OptOutText}>{languages.ownerInfoMssMissingText}</p>
            <p className={styles.OptOutQuestion}>{languages.ownerInfoMssMissingQuestion}</p>
          </div>
        </GenericModal>
      )}
    </>
  )
}

export default MssEmailEntry
